import { createContext, Dispatch, SetStateAction, useContext } from "react";
import { CellAlignment } from "./cell";
import { AdditionalDataProps, TableProps } from "./table";
import { Virtualizer } from "@tanstack/react-virtual";
import { HeaderGroup, Table } from "@tanstack/react-table";

export type CellAlignmentState = { id: string; alignment: CellAlignment }[];

export interface TableContextProps<T> extends TableProps<T> {
  /**Height of the table in pixels*/
  tableHeight: number;
  /**The scroll position of the table in pixels*/
  scrollPosition: number;
  /**The total scrollable width of the table, only applicable if the table is allowed to horizontally overflow*/
  scrollWidth: number;
  /**Width of the container holding the table in pixels*/
  containerWidth: number;
  /**Height of the container holding the table in pixels*/
  containerHeight: number;

  table: Table<T>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  virtualizer: Virtualizer<any, any>;

  /**Whether any of the columns within the table are being resized*/
  columnIsBeingResized: string;
  /**A dispatch that sets the state of columnIsBeingResized*/
  setColumnIsBeingResized: Dispatch<SetStateAction<string>>;

  /**The id of the currently dragged column (if any)*/
  draggedColumn: string | null;
  /**A dispatch that sets the id of draggedColumn*/
  setDraggedColumn: Dispatch<SetStateAction<string | null>>;

  /**A mapping of each column's alignment in a table, this will be used to align the header by
   * inferring alignment from body cells. This way, we don't need to define alignment twice*/
  columnAlignment: CellAlignmentState;

  /**A dispatch that updates the value of columnAlignment*/
  setColumnAlignment: Dispatch<SetStateAction<CellAlignmentState>>;

  /**The total number of rows*/
  count?: number;

  isMobile?: boolean;

  rowChildren?: AdditionalDataProps[];
  expandedRowHeight?: number;

  mergedHeaderGroups: HeaderGroup<T>[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const TableContext = createContext<TableContextProps<any> | undefined>(
  undefined,
);

export function useTable() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const context = useContext(TableContext);
  if (context) return context;
  throw new Error("useTable must be used within Table");
}
